import { makeAutoObservable, runInAction } from 'mobx';
import toastStore from '../../store/toast-store';
import {
  StripeConnectStatus,
  StripeStatusBadge,
  RevenueStats,
  PaymentDetails,
  RefundRequest,
  StripeConnectModel
} from '../../model/stripe/stripe-connect-model';

/**
 * ViewModel pour la gestion de Stripe Connect
 */
class StripeConnectViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private stripeConnectModel: StripeConnectModel;

  stripeStatus: StripeConnectStatus | null = null;

  revenueStats: RevenueStats | null = null;

  payments: PaymentDetails[] = [];
  paymentsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null = null;

  constructor() {
    makeAutoObservable(this);
    this.stripeConnectModel = StripeConnectModel.getInstance();
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  /**
   * Récupère le statut du compte Stripe Connect
   */
  fetchStripeStatus = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.getStatus();

      if (response.success && response.data) {
        runInAction(() => {
          this.stripeStatus = response.data!;
        });
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération du statut Stripe';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Crée un lien d'onboarding Stripe et redirige
   */
  startOnboarding = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.createOnboardingLink(
        window.location.href,
        window.location.href
      );

      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la création du lien d\'onboarding';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Ouvre le dashboard Stripe Express
   */
  openDashboard = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.createDashboardLink();

      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de l\'ouverture du dashboard';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Récupère les statistiques de revenus
   */
  fetchRevenueStats = async (period?: 'day' | 'week' | 'month' | 'all') => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.getRevenueStats(period);

      if (response.success && response.data) {
        runInAction(() => {
          this.revenueStats = response.data!;
        });
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération des statistiques';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Récupère la liste des paiements
   */
  fetchPayments = async (
    page: number = 1,
    limit: number = 20,
    status?: string,
    startDate?: string,
    endDate?: string
  ) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.getPayments(
        page,
        limit,
        status,
        startDate,
        endDate
      );

      if (response.success && response.data) {
        runInAction(() => {
          this.payments = response.data!.payments;
          this.paymentsPagination = response.data!.pagination;
        });
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération des paiements';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Récupère les détails d'un paiement
   */
  fetchPaymentDetails = async (paymentId: string): Promise<PaymentDetails | null> => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.getPaymentDetails(paymentId);

      if (response.success && response.data) {
        return response.data;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return null;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération du paiement';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Crée un remboursement
   */
  createRefund = async (paymentId: string, refundData: RefundRequest) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.stripeConnectModel.createRefund(paymentId, refundData);

      if (response.success) {
        toastStore.success('Remboursement effectué avec succès');

        await this.fetchPayments();
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors du remboursement';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Retourne le badge de statut pour l'affichage UI
   */
  get statusBadge(): StripeStatusBadge {
    if (!this.stripeStatus || !this.stripeStatus.isConnected) {
      return 'not_configured';
    }

    if (this.stripeStatus.chargesEnabled && this.stripeStatus.payoutsEnabled) {
      return 'verified';
    }

    if (this.stripeStatus.pastDue.length > 0) {
      return 'restricted';
    }

    return 'pending';
  }

  /**
   * Retourne le texte du badge de statut
   */
  get statusBadgeText(): string {
    switch (this.statusBadge) {
      case 'not_configured':
        return 'Non configuré';
      case 'pending':
        return 'En cours de vérification';
      case 'verified':
        return 'Vérifié';
      case 'restricted':
        return 'Restreint';
      default:
        return 'Inconnu';
    }
  }

  /**
   * Retourne la couleur du badge de statut
   */
  get statusBadgeColor(): string {
    switch (this.statusBadge) {
      case 'not_configured':
        return 'gray';
      case 'pending':
        return 'yellow';
      case 'verified':
        return 'green';
      case 'restricted':
        return 'red';
      default:
        return 'gray';
    }
  }

  /**
   * Vérifie si le compte peut accepter des paiements
   */
  get canAcceptPayments(): boolean {
    return this.stripeStatus?.chargesEnabled === true;
  }

  /**
   * Vérifie si le compte peut recevoir des virements
   */
  get canReceivePayouts(): boolean {
    return this.stripeStatus?.payoutsEnabled === true;
  }

  /**
   * Vérifie si l'onboarding est complet
   */
  get isOnboardingComplete(): boolean {
    return this.stripeStatus?.detailsSubmitted === true;
  }

  /**
   * Retourne le montant total des frais de service (compatible avec totalFees ou totalStripeFees)
   */
  get totalStripeFees(): number {
    if (!this.revenueStats) return 0;
    return this.revenueStats.totalStripeFees ?? this.revenueStats.totalFees ?? 0;
  }

  /**
   * Initialise les données
   */
  initialize = async () => {
    await this.fetchStripeStatus();

    // Si le compte est connecté, charger les stats et paiements
    if (this.stripeStatus?.isConnected) {
      await Promise.all([
        this.fetchRevenueStats(),
        this.fetchPayments()
      ]);
    }
  }

  /**
   * Formate un montant en euros
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  /**
   * Formate une date
   */
  formatDate(dateString: string): string {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  }
}

export const stripeConnectViewModel = new StripeConnectViewModel();
